import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { StructureNode } from '../types';
import { useAuth } from '../hooks/useAuth';

export interface StructureContextType {
  loading: boolean;
  structureNodes: StructureNode[];
}

const StructureContext = createContext<StructureContextType | undefined>(undefined);

export const StructureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [structureNodes, setStructureNodes] = useState<StructureNode[]>([]);
  const [loading, setLoading] = useState(true);
  const { authUser } = useAuth();

  useEffect(() => {
    if (!authUser) {
      setStructureNodes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(collection(db, 'structure_nodes'), orderBy('name', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nodes: StructureNode[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as StructureNode));

      setStructureNodes(nodes);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching structure nodes:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [authUser]);

  return (
    <StructureContext.Provider value={{ loading, structureNodes }}>
      {children}
    </StructureContext.Provider>
  );
};

export const useStructure = () => {
  const context = useContext(StructureContext);
  if (context === undefined) {
    throw new Error('useStructure must be used within a StructureProvider');
  }
  return context;
};
